export const privacyContent = `
  <h1>PrezziePal Privacy Policy</h1>
  <p><strong>Effective Date: March 12, 2025</strong></p>
  
  <h2>Introduction</h2>
  <p>Welcome to PrezziePal ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Service").</p>
  <p>Please read this Privacy Policy carefully. By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.</p>
  
  <h2>Information We Collect</h2>
  <h3>Personal Information You Provide</h3>
  <p>We collect information that you voluntarily provide when using our Service, including:</p>
  <ul>
    <li><strong>Account Information</strong>: When you create an account, we collect your name, email address, phone number, and authentication credentials.</li>
    <li><strong>Profile Information</strong>: Information you add to your profile such as birth date, profile picture, and personal preferences.</li>
    <li><strong>Contact Information</strong>: When you choose to sync your contacts, PrezziePal requires access to names, phone numbers, email addresses, and birthdays from your device's contact list with your permission. This helps users connect with friends and family for gift tracking. All contact processing happens on-device, and we do not upload contacts to our servers.</li>
    <li><strong>Wishlist Data</strong>: Information about items you add to your wishlists, including product details, prices, and links.</li>
  </ul>
  
  <h3>Information Automatically Collected</h3>
  <p>When you use our Service, we automatically collect certain information, including:</p>
  <ul>
    <li><strong>Device Information</strong>: Device type, operating system, unique device identifiers, IP address, mobile network information, and browser type.</li>
    <li><strong>Usage Information</strong>: How you use our Service, including features accessed, time spent, and interactions.</li>
    <li><strong>Location Information</strong>: General location based on IP address.</li>
  </ul>
  
  <h3>Information from Third Parties</h3>
  <p>We may receive information about you from third parties, including:</p>
  <ul>
    <li>Authentication providers (Google, Apple) when you choose to sign in using these services.</li>
    <li>Other PrezziePal users who include you in their contacts.</li>
    <li>Third-party retailers when you interact with product links.</li>
  </ul>
  
  <h2>How We Use Your Information</h2>
  <p>We use your information for the following purposes:</p>
  <ul>
    <li><strong>Provide and maintain our Service</strong>, including processing transactions, sending notifications, and providing customer support.</li>
    <li><strong>Personalize your experience</strong> by delivering content and product suggestions relevant to your interests.</li>
    <li><strong>Improve our Service</strong> by analyzing usage patterns and trends.</li>
    <li><strong>Facilitate social features</strong> including contact synchronization and wishlist sharing. We use contact data only to display name and birthday information within the app.</li>
    <li><strong>Send notifications</strong> about upcoming birthdays, wishlist activity, and other relevant updates.</li>
    <li><strong>Business purposes</strong> including marketing, business development, and partnership opportunities. We do not use contact data for business purposes.</li>
    <li><strong>Security and fraud prevention</strong> to protect our Service and users.</li>
    <li><strong>Comply with legal obligations</strong> as required by applicable laws and regulations.</li>
  </ul>
  
  <h2>How We Share Your Information</h2>
  <h3>With Other Users</h3>
  <ul>
    <li>Your profile information is shared with your contacts on PrezziePal.</li>
    <li>Wishlist items marked as public are visible to your connections.</li>
  </ul>
  
  <h3>With Third-Party Partners and Advertisers</h3>
  <ul>
    <li>We may share aggregated or anonymized usage data with third parties for analytical, commercial, or marketing purposes. This never includes contact information from your device.</li>
    <li>Information about your interactions with the Service may be shared with advertisers and marketing partners.</li>
  </ul>
  
  <h3>With Service Providers</h3>
  <p>We may share your information with third-party vendors, service providers, and other business partners who perform services on our behalf, such as:</p>
  <ul>
    <li>Cloud storage providers</li>
    <li>Analytics services</li>
    <li>Customer service and communication platforms</li>
    <li>Payment processors</li>
  </ul>
  <p>We do not share your device contacts with any service providers.</p>
  
  <h3>For Legal Reasons</h3>
  <p>We may disclose your information:</p>
  <ul>
    <li>To comply with any court order, law, or legal process</li>
    <li>To enforce our Terms of Service</li>
    <li>To protect the rights, property, or safety of PrezziePal, our users, or others</li>
  </ul>
  
  <h3>Business Transfers</h3>
  <p>If PrezziePal is involved in a merger, acquisition, or sale of all or a portion of its assets, your information may be transferred as part of that transaction.</p>
  
  <h2>Third-Party Services</h2>
  <p>Our Service integrates with various third-party services for functionality such as:</p>
  <ul>
    <li>Push notifications</li>
    <li>Deep linking and invites</li>
    <li>Image storage and processing</li>
    <li>Database and backend services</li>
    <li>Authentication services</li>
  </ul>
  <p>These services have their own privacy policies, and we encourage you to review them. We may change the third-party services we use at any time in our sole discretion, with or without notice to you.</p>
  
  <h2>Data Protection</h2>
  <p>As a UK-based application, we comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. We serve as the data controller for personal information collected through our Service.</p>
  <p>For users in the European Economic Area (EEA), we may transfer your personal information to countries outside the EEA. When we do so, we ensure appropriate safeguards are in place in compliance with UK data protection laws.</p>
  <p>We protect user data through encryption, secure storage, and by not transmitting contacts off-device. All contact processing happens locally on your device.</p>
  
  <h2>Your Data Protection Rights</h2>
  <p>Under UK data protection law, you have the following rights:</p>
  <ul>
    <li><strong>Right to be informed</strong> about how we use your personal data</li>
    <li><strong>Right of access</strong> to your personal information</li>
    <li><strong>Right to rectification</strong> of inaccurate or incomplete information</li>
    <li><strong>Right to erasure</strong> (or "right to be forgotten") in certain circumstances</li>
    <li><strong>Right to restrict processing</strong> of your personal data</li>
    <li><strong>Right to data portability</strong>, allowing you to obtain and reuse your data</li>
    <li><strong>Right to object</strong> to processing of your data for certain purposes</li>
    <li><strong>Rights related to automated decision making and profiling</strong></li>
  </ul>
  <p>To exercise these rights, please contact us at hello@prezziepal.com. We will respond to your request within one month. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe we have not handled your personal data in accordance with data protection law.</p>
  
  <h3>Contact Permissions</h3>
  <p>When you first use contact synchronization features, we'll request permission to access your device's contacts. This access is required for PrezziePal's core functionality of helping users connect with friends and family for gift tracking. You can manage these permissions through your device settings. Remember that all contact processing happens on your device, and contact information is never uploaded to our servers or shared with third parties.</p>
  
  <h3>Push Notifications</h3>
  <p>You can opt out of receiving push notifications by changing your settings at the device level.</p>
  
  <h2>Children's Privacy</h2>
  <p>Our Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If we learn we have collected personal information from a child under 13, we will delete this information.</p>
  
  <h2>Changes to This Privacy Policy</h2>
  <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top of this page. You are advised to review this Privacy Policy periodically for any changes.</p>
  
  <h2>Contact Us</h2>
  <p>If you have any questions about this Privacy Policy, please contact us at:</p>
  <ul>
    <li>Email: hello@prezziepal.com</li>
  </ul>
`;
